import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import type { AgentQuestion } from "@/hooks/use-agent-questions";
import { useTranslations } from "@/hooks/use-translations";

/**
 * The ads agent waits for the person to confirm a deletion (M-05). Only these
 * buttons answer it; the composer is blocked until one is pressed.
 */
export function DeletionConfirm({
	question,
	onChoose,
	disabled,
}: {
	question: AgentQuestion;
	onChoose: (choice: "delete" | "cancel") => void;
	disabled?: boolean;
}) {
	const t = useTranslations("workspace");
	const target = question.deletion;
	const what = target ? t(`deletion.levels.${target.level}`) : "";
	return (
		<motion.div
			initial={{ opacity: 0, y: 6 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.2 }}
			role="alertdialog"
			aria-label={t("deletion.title")}
			className="mb-2 rounded-2xl border border-border-subtle bg-bg-surface p-3 text-sm text-text-primary"
		>
			<p className="font-semibold">{t("deletion.title")}</p>
			<p className="mt-1 text-text-secondary">
				{target
					? t(target.name ? "deletion.prompt" : "deletion.promptNoName", { what, name: target.name, id: target.id })
					: question.question}
			</p>
			<div className="mt-3 flex flex-wrap gap-2">
				<Button variant="danger" size="sm" disabled={disabled} onClick={() => onChoose("delete")}>
					{t("deletion.delete")}
				</Button>
				<Button variant="secondary" size="sm" disabled={disabled} onClick={() => onChoose("cancel")}>
					{t("deletion.cancel")}
				</Button>
			</div>
		</motion.div>
	);
}
